import { useState } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
  AlertTriangle,
  Briefcase,
  BookOpen,
  Calendar,
  ChevronDown,
  Clock,
  DollarSign,
  Download,
  ExternalLink,
  Eye,
  EyeOff,
  FileText,
  Link2,
  Lock,
  LucideProps,
  MapPin,
  Pencil,
  X,
} from 'lucide-react';
import ProjectCriteriaFit from './ProjectCriteriaFit';
import type { ProjectCriteriaFitRow } from './ProjectCriteriaFit';
import SaveBar from './SaveBar';
import Toast from './Toast';
import type { OnePagerContent, OnePagerWarning } from '../services/onePagersService';
import { decodeEscapedHtml, sanitizeHtml, stripHtmlToText } from '../utils/html';
import AppBannerBackdrop from './AppBannerBackdrop';
import type {
  SalesforceProviderAvailabilityResponse,
  SalesforceProviderFeesResponse,
} from '../services/salesforceService';

type IconType = React.ComponentType<LucideProps>;

export function normalizeWebsiteUrl(raw: string) {
  const trimmed = raw.trim();
  if (!trimmed) return '';
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) return trimmed;
  return `https://${trimmed}`;
}

// SF location values look like "US-CA-Los Angeles" (country-state-city); render as "Los Angeles, CA".
export function formatLocationCityState(raw?: string): string | undefined {
  if (!raw?.trim()) return undefined;
  const parts = raw.split('-').map((p) => p.trim()).filter(Boolean);
  if (parts.length < 3) return raw;
  const state = parts[1];
  const city = parts.slice(2).join('-');
  return `${city}, ${state}`;
}

function warningKey(warning: { category?: string; message?: string }) {
  return `${warning.category || ''}::${warning.message || ''}`;
}

function Chip({ icon: Icon, href, children }: { icon: IconType; href?: string; children: React.ReactNode }) {
  // Solid white border + white text per BPP-231 (matches the PDF banner chips).
  const className =
    "inline-flex items-center gap-[6px] rounded-full border border-white bg-white/[0.10] px-[11px] py-[5px] font-['Inter',sans-serif] text-[12.5px] font-medium text-white transition-colors hover:bg-white/[0.24]";
  if (href) {
    return (
      <a href={href} target="_blank" rel="noreferrer" className={className}>
        <Icon size={13} />
        {children}
      </a>
    );
  }
  return (
    <span className={className}>
      <Icon size={13} />
      {children}
    </span>
  );
}

export type InterimEditableField = 'general_bio' | 'snapshot' | 'why_we_recommend';

// Rich-text toolbar shared with the Group one-pager's Snapshot/WhyWe editors, so Interim
// narratives get the same controls — text style, bold/italic/underline, lists, link, clear
// (BPP-389).
const RICH_TEXT_TOOLBAR = [
  [{ header: [1, 2, false] }],
  ['bold', 'italic', 'underline'],
  [{ list: 'ordered' }, { list: 'bullet' }],
  ['link'],
  ['clean'],
];

function EditButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      data-pdf-exclude
      onClick={onClick}
      className="ml-auto flex items-center gap-[6px] rounded-[6px] px-[6px] py-[4px] font-['Inter',sans-serif] text-[13.5px] font-semibold text-[#25ADE3] transition-colors hover:bg-[#25ADE3]/10"
    >
      <Pencil size={14} />
      Edit
    </button>
  );
}

// Edit state for one rich-text section. The draft lives in `editorHtml` while editing; Save
// commits it as a new version through onCommit, Cancel discards it. Stored HTML can arrive
// entity-escaped (BPP-226), so it is decoded before seeding the editor; clean Quill output
// round-trips unchanged, so a Save with no real edit is skipped (matching the old behaviour).
function useRichTextSection(
  html: string | undefined,
  onCommit?: (html: string) => Promise<boolean> | void,
) {
  const initialHtml = html ? decodeEscapedHtml(html) : '';
  const [editing, setEditing] = useState(false);
  const [editorHtml, setEditorHtml] = useState('');
  const [saving, setSaving] = useState(false);

  const startEditing = () => {
    setEditorHtml(initialHtml);
    setEditing(true);
  };

  const cancelEditing = () => {
    if (saving) return;
    setEditing(false);
    setEditorHtml('');
  };

  // Await the save and keep the editor open if it fails, so the user's unsaved edit survives
  // a failed save and can be retried (BPP-389 review follow-up). While a save is in flight the
  // section is locked (`saving`): a second Save is ignored so it can't open a duplicate Draft
  // version, and the editor is held read-only so edits typed during the await aren't discarded
  // when the first save settles (BPP-389 review follow-up).
  const finishEditing = async () => {
    if (saving) return;
    if (editorHtml !== initialHtml) {
      setSaving(true);
      let saved: boolean | void;
      try {
        saved = await onCommit?.(editorHtml);
      } finally {
        setSaving(false);
      }
      if (saved === false) return;
    }
    setEditing(false);
    setEditorHtml('');
  };

  return { editing, editorHtml, saving, setEditorHtml, startEditing, cancelEditing, finishEditing };
}

type RichTextSection = ReturnType<typeof useRichTextSection>;

function RichTextBody({
  section,
  html,
  emptyLabel,
}: {
  section: RichTextSection;
  html?: string;
  emptyLabel: string;
}) {
  if (section.editing) {
    return (
      <div className="space-y-2" data-pdf-exclude>
        <div className="flex items-center gap-2">
          <div className="flex-1" />
          <button
            type="button"
            className="rounded bg-[#132543] px-3 py-1 text-white disabled:opacity-60"
            onClick={section.finishEditing}
            disabled={section.saving}
          >
            {section.saving ? 'Saving…' : 'Save'}
          </button>
          <button
            type="button"
            className="rounded border border-gray-200 px-3 py-1 text-[#132543] disabled:opacity-60"
            onClick={section.cancelEditing}
            disabled={section.saving}
          >
            Cancel
          </button>
        </div>
        <div className="snapshot-quill overflow-hidden rounded-lg border border-gray-200 bg-gray-50">
          <ReactQuill
            theme="snow"
            value={section.editorHtml}
            onChange={section.setEditorHtml}
            readOnly={section.saving}
            modules={{ toolbar: RICH_TEXT_TOOLBAR }}
          />
        </div>
      </div>
    );
  }

  if (!html?.trim()) {
    return <p className="italic text-[#60667C]">{emptyLabel}</p>;
  }

  // Render stored HTML through the same ql-editor wrapper the Group one-pager uses, so headings
  // and lists display with the editor's styling (Tailwind's preflight strips those otherwise),
  // and always sanitise + decode first (BPP-226).
  return (
    <div
      className="interim-html ql-snow [&_em]:italic [&_i]:italic"
      dangerouslySetInnerHTML={{ __html: `<div class="ql-editor">${sanitizeHtml(decodeEscapedHtml(html))}</div>` }}
    />
  );
}

function CollapsibleSection({
  title,
  editable,
  children,
}: {
  title: React.ReactNode;
  editable?: { html?: string; emptyLabel: string; onCommit?: (html: string) => Promise<boolean> | void };
  children?: React.ReactNode;
}) {
  const [open, setOpen] = useState(true);
  const section = useRichTextSection(editable?.html, editable?.onCommit);

  return (
    <section className="mt-[38px]">
      <div className="mb-[16px] flex items-center gap-[11px]">
        <button
          type="button"
          data-pdf-exclude
          onClick={() => setOpen((o) => !o)}
          aria-label={open ? 'Collapse section' : 'Expand section'}
          className="flex text-[#132543] transition-transform duration-150"
          style={{ transform: open ? 'none' : 'rotate(-90deg)' }}
        >
          <ChevronDown size={16} />
        </button>
        <h3 className="font-['Roboto_Slab',serif] text-[19px] font-bold text-[#132543]">{title}</h3>
        {editable && !section.editing && <EditButton onClick={section.startEditing} />}
      </div>
      {open && (
        <div className="rounded-[8px]">
          {editable ? (
            <RichTextBody section={section} html={editable.html} emptyLabel={editable.emptyLabel} />
          ) : (
            children
          )}
        </div>
      )}
    </section>
  );
}

function WarningsBanner({
  warnings,
  dismissedWarnings,
  onDismiss,
}: {
  warnings?: OnePagerWarning[];
  dismissedWarnings?: string[];
  onDismiss?: (warning: OnePagerWarning) => void;
}) {
  const dismissedKeys = new Set(dismissedWarnings ?? []);
  const visible = (warnings ?? []).filter((w) => !dismissedKeys.has(warningKey(w)));
  if (visible.length === 0) return null;

  return (
    <div className="mt-[24px] space-y-[8px]" data-pdf-exclude>
      {visible.map((w, i) => (
        <div
          key={i}
          className="group relative flex items-start gap-[10px] rounded-[10px] border border-[#FDE68A] bg-[#FFFBEB] px-[16px] py-[11px] pr-[38px]"
        >
          <AlertTriangle size={16} className="mt-[2px] shrink-0" style={{ color: '#92400E' }} />
          <div className="text-[13.5px] text-[#7B3306]">
            <span className="font-semibold capitalize">{w.category?.replace(/_/g, ' ') || 'Warning'}</span>
            {w.severity ? <span className="ml-[6px] text-[11.5px] uppercase text-[#92400E]">{w.severity}</span> : null}
            {/* Messages may arrive as HTML from the MLE; dismissal keys still use the raw string. */}
            <div className="mt-[2px]">{stripHtmlToText(w.message)}</div>
          </div>
          {onDismiss && (
            <button
              type="button"
              onClick={() => onDismiss(w)}
              aria-label="Dismiss concern"
              className="absolute right-[10px] top-[10px] flex h-[22px] w-[22px] items-center justify-center rounded-full text-[#92400E] opacity-0 transition-opacity hover:bg-[#92400E]/10 focus-visible:opacity-100 group-hover:opacity-100 group-focus-within:opacity-100 [@media(hover:none)]:opacity-100"
            >
              <X size={13} />
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

export default function InterimOnePager({
  fullName,
  tagline,
  location,
  website,
  salesforceFilesUrl,
  resumeUrl,
  resumeFileName,
  resumeAttachedDate,
  content,
  sfAvailability,
  sfFees,
  projectCriteriaFitRows,
  draftProviderFitByKey,
  onDraftProviderFitChange,
  onCancelCriteriaFitDraft,
  onSaveSection,
  saveCommitId,
  missingInputs: missingInputsProp,
  dismissedWarnings,
  onDismissWarning,
  generationLoading,
  generationError,
  onRetryGeneration,
  status = 'draft',
  statusLabel,
  saving,
  savingError,
  onSave,
  publishing,
  publishingError,
  onPublishClick,
  publishDisabled,
  publishDisabledReason,
  showSendEmail,
  onSendEmail,
  onRegenerateClick,
  regenerateDisabled,
  lastEditedDate,
  lastEditedTime,
  lastEditedBy,
  onDownload,
}: {
  fullName?: string;
  tagline?: string;
  location?: string;
  website?: string;
  // Live from Salesforce — link to the account's Files related list, used by the resume card.
  salesforceFilesUrl?: string;
  resumeUrl?: string | null;
  resumeFileName?: string | null;
  resumeAttachedDate?: string | null;
  content?: OnePagerContent;
  // Live from Salesforce (Introduction__c) — always current, never drifts from the generated version.
  sfAvailability?: SalesforceProviderAvailabilityResponse;
  sfFees?: SalesforceProviderFeesResponse;
  // KCS table (BPP-208): labels/needs live from Salesforce criteria, provider fit from content.criteria_fit.
  projectCriteriaFitRows?: ProjectCriteriaFitRow[];
  draftProviderFitByKey?: Record<string, string>;
  onDraftProviderFitChange?: (next: Record<string, string>) => void;
  onCancelCriteriaFitDraft?: () => void;
  // Persists a single edited HTML section as a new Draft version; rejects if the save fails.
  onSaveSection?: (field: InterimEditableField, html: string) => Promise<void>;
  saveCommitId?: number;
  // Live-derived from Salesforce/resume lookups by the caller — see App.tsx. Falls back to
  // content.missing_inputs (the MLE's generation-time snapshot) only if the caller omits it.
  missingInputs?: string[];
  dismissedWarnings?: string[];
  onDismissWarning?: (warning: OnePagerWarning) => void;
  generationLoading?: boolean;
  generationError?: string;
  onRetryGeneration?: () => void;
  status?: 'draft' | 'published';
  // Full status wording from the page (e.g. "Published (Draft Pending)"). The bare `status`
  // flag can't express a published one-pager that has since been edited, which made the pill
  // read "Draft" while Send Email stayed available — the group view has always shown the
  // longer label (BPP-262). Falls back to the flag when not supplied.
  statusLabel?: string;
  saving?: boolean;
  savingError?: string;
  onSave?: () => void;
  publishing?: boolean;
  publishingError?: string;
  onPublishClick?: () => void;
  publishDisabled?: boolean;
  publishDisabledReason?: string;
  showSendEmail?: boolean;
  onSendEmail?: () => void;
  onRegenerateClick?: () => void;
  regenerateDisabled?: boolean;
  lastEditedDate?: string;
  lastEditedTime?: string;
  lastEditedBy?: string;
  onDownload?: () => void;
}) {
  const displayFullName = fullName?.trim() || 'Provider';
  const displayFirstName = displayFullName.split(' ')[0];

  const missingInputs = missingInputsProp ?? content?.missing_inputs ?? [];

  const effectiveResumeUrl = content?.resume_url ?? resumeUrl ?? undefined;
  const effectiveWebsiteUrl = website ? normalizeWebsiteUrl(website) : '';
  const effectiveLocation = formatLocationCityState(content?.location?.base_city || location);

  const [introNotesVisible, setIntroNotesVisible] = useState(true);
  const [toastMsg, setToastMsg] = useState<string | null>(null);

  const handleSectionSaved = () => {
    setToastMsg('Changes saved');
  };

  const commitSection = (field: InterimEditableField) => async (html: string): Promise<boolean> => {
    if (!onSaveSection) return true;
    try {
      await onSaveSection(field, html);
      handleSectionSaved();
      return true;
    } catch (e) {
      console.error('[InterimOnePager] section save failed', field, e);
      setToastMsg("Couldn't save changes — please try again");
      return false;
    }
  };

  const overview = useRichTextSection(content?.general_bio, commitSection('general_bio'));

  const resolvedStatusLabel = statusLabel ?? (status === 'published' ? 'Published' : 'Draft');

  const handleSendEmailClick = () => {
    onSendEmail?.();
  };

  const handleDownload = () => {
    onDownload?.();
  };

  const isLoadingFirstDraft = generationLoading && !content;

  if (isLoadingFirstDraft) {
    return (
      <div className="flex flex-col items-center justify-center gap-[16px] py-[120px] text-center">
        <div className="h-[28px] w-[28px] animate-spin rounded-full border-[3px] border-[#25ADE3] border-t-transparent" />
        <p className="font-['Inter',sans-serif] text-[15px] text-[#60667C]">
          Generating the interim one-pager for {displayFullName}…
        </p>
      </div>
    );
  }

  if (generationError && !content) {
    return (
      <div className="mx-auto flex max-w-[560px] flex-col items-center gap-[16px] py-[100px] text-center">
        <AlertTriangle size={28} style={{ color: '#B45309' }} />
        <p className="font-['Roboto_Slab',serif] text-[17px] font-bold text-[#132543]">
          Couldn't generate the one-pager
        </p>
        <p className="font-['Inter',sans-serif] text-[14px] text-[#60667C]">{generationError}</p>
        {onRetryGeneration && (
          <button
            type="button"
            onClick={onRetryGeneration}
            className="rounded-[8px] bg-[#132543] px-[18px] py-[9px] font-['Inter',sans-serif] text-[14px] font-semibold text-white"
          >
            Retry
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="relative pb-[88px]">
      {/* Banner with status pill, download action, and identity — wave backdrop per BPP-231 */}
      <div
        className="relative h-banner w-full overflow-hidden rounded-[14px] border border-[rgba(19,37,67,0.12)]"
        style={{ background: '#0C1930' }}
      >
        <AppBannerBackdrop />
        <div className="absolute left-[18px] top-[18px]" data-pdf-exclude>
          <span
            className="inline-flex items-center gap-[7px] whitespace-nowrap rounded-full border px-[12px] py-[5px] font-['Inter',sans-serif] text-[12px] font-semibold text-white"
            style={{ borderColor: 'rgba(255,255,255,.28)', background: 'rgba(255,255,255,.16)' }}
          >
            <span
              className="h-[7px] w-[7px] shrink-0 rounded-full"
              style={{ background: resolvedStatusLabel.startsWith('Published') ? '#34d399' : '#FBBF2B' }}
            />
            {resolvedStatusLabel}
          </span>
        </div>

        <button
          type="button"
          data-pdf-exclude
          onClick={handleDownload}
          className="absolute right-[18px] top-[18px] flex items-center gap-[7px] rounded-[8px] bg-white/95 px-[14px] py-[8px] font-['Inter',sans-serif] text-[13px] font-semibold text-[#132543] shadow-[0_2px_8px_rgba(0,0,0,0.18)] transition-colors hover:bg-white"
        >
          <Download size={15} /> Download Report
        </button>

        <div className="absolute inset-x-0 bottom-0 flex flex-col gap-[11px] px-[32px] pb-[20px]">
          <div>
            <p className="font-['Roboto_Slab',serif] text-[27px] font-extrabold leading-[1.1] text-white">
              {displayFullName}
            </p>
            {tagline && (
              <p className="mt-[5px] font-['Open_Sans',sans-serif] text-[14.5px] font-medium text-white">{tagline}</p>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-[8px]">
            {effectiveLocation && <Chip icon={MapPin}>{effectiveLocation}</Chip>}
            {effectiveWebsiteUrl && (
              <Chip icon={Link2} href={effectiveWebsiteUrl}>
                LinkedIn
              </Chip>
            )}
            {effectiveResumeUrl && (
              <Chip icon={FileText} href={effectiveResumeUrl}>
                Resume
              </Chip>
            )}
          </div>
        </div>
      </div>

      <WarningsBanner
        warnings={content?.warnings}
        dismissedWarnings={dismissedWarnings}
        onDismiss={onDismissWarning}
      />

      {/* Executive Overview — editable, but not collapsible */}
      <div className="mt-[40px] rounded-[12px] bg-[#DCEDF7] px-[26px] py-[22px]">
        <div className="mb-[12px] flex items-center gap-[10px]">
          <BookOpen size={19} className="text-[#4BABE0]" />
          <strong className="font-['Roboto_Slab',serif] text-[17px] font-bold text-[#132543]">
            Executive Overview
          </strong>
          {!overview.editing && <EditButton onClick={overview.startEditing} />}
        </div>
        <div className="space-y-[14px] rounded-[8px] font-['Open_Sans',sans-serif] text-[15px] leading-[1.6] text-[#132543]">
          <RichTextBody
            section={overview}
            html={content?.general_bio}
            emptyLabel="Executive overview not yet generated."
          />
        </div>
      </div>

      <CollapsibleSection
        title="Snapshot — Why They're The Right Fit"
        editable={{
          html: content?.snapshot,
          emptyLabel: 'Snapshot not yet generated.',
          onCommit: commitSection('snapshot'),
        }}
      />

      <CollapsibleSection
        title={
          <>
            Why <em className="italic">we</em> recommend {displayFirstName}
          </>
        }
        editable={{
          html: content?.why_we_recommend,
          emptyLabel: 'Recommendation not yet generated.',
          onCommit: commitSection('why_we_recommend'),
        }}
      />

      {(projectCriteriaFitRows?.length ?? 0) > 0 && (
        <div className="mt-[38px]">
          <ProjectCriteriaFit
            rows={projectCriteriaFitRows}
            draftProviderFitByKey={draftProviderFitByKey}
            onDraftProviderFitChange={onDraftProviderFitChange}
            onCancelDraft={onCancelCriteriaFitDraft}
            saveCommitId={saveCommitId}
          />
        </div>
      )}

      <CollapsibleSection title="Availability">
        <div
          className="overflow-hidden rounded-[12px] border"
          style={{
            borderColor: 'rgba(171,145,81,.45)',
            background: 'linear-gradient(180deg, rgba(171,145,81,.14), rgba(171,145,81,.05))',
          }}
        >
          <div
            className="flex items-center gap-[9px] px-[22px] py-[13px]"
            style={{ background: 'rgba(171,145,81,.18)', borderBottom: '1px solid rgba(171,145,81,.32)' }}
          >
            <Calendar size={17} style={{ color: '#AB9151' }} />
            <strong
              className="font-['Roboto_Slab',serif] text-[15px] font-bold tracking-[0.01em]"
              style={{ color: '#132543' }}
            >
              AVAILABILITY
            </strong>
          </div>
          <div className="grid grid-cols-3">
            <div className="border-r px-[22px] py-[18px]" style={{ borderColor: 'rgba(171,145,81,.28)' }}>
              <div
                className="mb-[8px] flex items-center gap-[7px] font-['Inter',sans-serif] text-[11px] font-bold uppercase tracking-[.07em]"
                style={{ color: '#AB9151' }}
              >
                <Clock size={14} /> Start Date
              </div>
              <div className="text-[14.5px] font-bold leading-[1.35] text-[#132543]">
                {sfAvailability?.start?.value || 'Not yet available'}
              </div>
            </div>
            <div className="border-r px-[22px] py-[18px]" style={{ borderColor: 'rgba(171,145,81,.28)' }}>
              <div
                className="mb-[8px] flex items-center gap-[7px] font-['Inter',sans-serif] text-[11px] font-bold uppercase tracking-[.07em]"
                style={{ color: '#AB9151' }}
              >
                <Briefcase size={14} /> On-Site Commitment
              </div>
              <div className="text-[14.5px] font-bold leading-[1.35] text-[#132543]">
                {sfAvailability?.onsite?.value || 'Not yet available'}
              </div>
            </div>
            <div className="px-[22px] py-[18px]">
              <div
                className="mb-[8px] flex items-center gap-[7px] font-['Inter',sans-serif] text-[11px] font-bold uppercase tracking-[.07em]"
                style={{ color: '#AB9151' }}
              >
                <MapPin size={14} /> Location
              </div>
              <div className="text-[14.5px] font-bold leading-[1.35] text-[#132543]">
                {effectiveLocation || 'Not specified'}
              </div>
            </div>
          </div>
        </div>
      </CollapsibleSection>

      <CollapsibleSection title="Engagement & Documentation">
        <div className="grid grid-cols-[1.1fr_1fr] gap-[18px]">
          <div className="rounded-[12px] border border-[#E2E2E2] bg-white px-[24px] py-[22px]">
            <div className="flex items-center gap-[7px] font-['Inter',sans-serif] text-[11px] font-bold uppercase tracking-[.07em] text-[#60667C]">
              <DollarSign size={14} className="text-[#132543]" /> Engagement Rate
            </div>
            {sfFees?.value ? (
              <div className="mt-[8px] font-['Roboto_Slab',serif] text-[30px] font-extrabold tracking-[-0.01em] text-[#132543]">
                {sfFees.value}
              </div>
            ) : (
              <div className="mt-[8px] font-['Inter',sans-serif] text-[14.5px] italic text-[#60667C]">
                Rate not yet available
              </div>
            )}
          </div>
          <div
            data-pdf-exclude
            className={
              'flex flex-col justify-center gap-[6px] rounded-[12px] border px-[24px] py-[22px] ' +
              (effectiveResumeUrl ? 'border-[#E2E2E2] bg-white' : 'border-[#FDE68A] bg-[#FFFBEB]')
            }
          >
            <div
              className={
                "flex items-center gap-[7px] font-['Inter',sans-serif] text-[11px] font-bold uppercase tracking-[.07em] " +
                (effectiveResumeUrl ? 'text-[#60667C]' : 'text-[#92400E]')
              }
            >
              {effectiveResumeUrl ? <FileText size={14} className="text-[#132543]" /> : <AlertTriangle size={14} />}
              Full Documentation
            </div>
            {effectiveResumeUrl ? (
              <>
                <div className="text-[14.5px] font-bold leading-[1.35] text-[#132543]">Resume attached</div>
                <div className="text-[12.5px] text-[#60667C]">
                  {resumeFileName || 'Resume'}
                  {resumeAttachedDate &&
                    ` · Attached ${new Date(resumeAttachedDate).toLocaleDateString(undefined, {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                    })}`}
                </div>
                <div className="mt-[8px] flex flex-wrap gap-[8px]">
                  <a
                    href={effectiveResumeUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex w-fit items-center gap-[7px] rounded-full border-[1.5px] border-[#25ADE3] px-[14px] py-[7px] font-['Inter',sans-serif] text-[13px] font-bold text-[#25ADE3] transition-colors hover:border-[#132543] hover:bg-[#132543] hover:text-white"
                  >
                    <FileText size={14} /> View
                  </a>
                  {salesforceFilesUrl && (
                    <a
                      href={salesforceFilesUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex w-fit items-center gap-[7px] rounded-full border-[1.5px] border-[#132543]/25 px-[14px] py-[7px] font-['Inter',sans-serif] text-[13px] font-bold text-[#132543] transition-colors hover:border-[#132543] hover:bg-[#132543] hover:text-white"
                    >
                      <ExternalLink size={14} /> Open in Salesforce
                    </a>
                  )}
                </div>
              </>
            ) : (
              <>
                <div className="text-[14.5px] font-bold leading-[1.35] text-[#7B3306]">Resume not yet attached</div>
                <div className="text-[12.5px] leading-[1.5] text-[#7B3306]">
                  Anchor pulls resumes from Salesforce. Upload the PDF to the Files tab on this provider's Salesforce
                  account, then regenerate this one-pager.
                </div>
                {salesforceFilesUrl && (
                  <a
                    href={salesforceFilesUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-[8px] inline-flex w-fit items-center gap-[7px] rounded-full border-[1.5px] border-[#B45309] px-[14px] py-[7px] font-['Inter',sans-serif] text-[13px] font-bold text-[#B45309] transition-colors hover:bg-[#B45309] hover:text-white"
                  >
                    <ExternalLink size={14} /> Open Provider in Salesforce
                  </a>
                )}
              </>
            )}
          </div>
        </div>
      </CollapsibleSection>

      <div
        className="mt-[38px] overflow-hidden rounded-[12px] border bg-white"
        style={{ borderColor: '#FCD34D' }}
        data-pdf-exclude
      >
        <div
          className="flex items-center gap-[9px] px-[22px] py-[13px]"
          style={{ background: '#FFFBEB', borderBottom: '1px solid #FDE68A' }}
        >
          <Lock size={15} style={{ color: '#B45309' }} />
          <strong className="text-[15px] font-semibold" style={{ color: '#7B3306' }}>
            Intro Notes
          </strong>
          <span
            className="rounded-[5px] px-[8px] py-[2px] text-[11.5px] font-semibold"
            style={{ background: '#FEE685', color: '#973C00' }}
          >
            Internal Only
          </span>
          <button
            type="button"
            onClick={() => setIntroNotesVisible((v) => !v)}
            className="ml-auto flex cursor-pointer"
            style={{ color: '#E17100' }}
            aria-label={introNotesVisible ? 'Hide internal notes' : 'Show internal notes'}
          >
            {introNotesVisible ? <Eye size={16} /> : <EyeOff size={16} />}
          </button>
        </div>
        {introNotesVisible ? (
          missingInputs.length > 0 ? (
            <ul className="flex flex-col gap-[11px] px-[24px] py-[18px]">
              {missingInputs.map((m, i) => (
                <li key={i} className="flex gap-[11px] text-[14px] leading-[1.55] text-[#132543]">
                  <span className="mt-[8px] h-[6px] w-[6px] shrink-0 rounded-full" style={{ background: '#E8A23D' }} />
                  <span>Missing input: {m}</span>
                </li>
              ))}
            </ul>
          ) : (
            <div className="px-[24px] py-[16px] text-[13px] italic text-[#60667C]">No open items.</div>
          )
        ) : (
          <div className="px-[24px] py-[16px] text-[13px] italic text-[#60667C]">
            Internal notes hidden — click the eye to reveal.
          </div>
        )}
      </div>

      <div className="mt-6" data-pdf-exclude>
        <SaveBar
          showSave
          showSendEmail={showSendEmail}
          showRegenerate
          lastEditedDate={lastEditedDate}
          lastEditedTime={lastEditedTime}
          createdBy={lastEditedBy}
          onSave={() => {
            onSave?.();
            handleSectionSaved();
          }}
          onSendEmail={handleSendEmailClick}
          onRegenerate={onRegenerateClick}
          onPublish={onPublishClick}
          saveDisabled={saving || publishing}
          saveLoading={saving}
          regenerateDisabled={regenerateDisabled || saving || publishing}
          publishDisabled={publishDisabled || saving || publishing}
          publishLoading={publishing}
          publishLabel={status === 'published' && publishDisabled ? 'Published' : undefined}
        />
        {publishDisabled && publishDisabledReason && (
          <div className="mx-auto w-full max-w-page px-4 pt-2" data-pdf-exclude>
            <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-800">
              {publishDisabledReason}
            </div>
          </div>
        )}
        {(savingError || publishingError) && (
          <div className="mx-auto w-full max-w-page px-4 pt-2" data-pdf-exclude>
            <div className="rounded-md border border-red-200 bg-white px-4 py-2 text-sm text-red-700">
              {savingError || publishingError}
            </div>
          </div>
        )}
      </div>

      {toastMsg && <Toast message={toastMsg} onDismiss={() => setToastMsg(null)} />}
    </div>
  );
}
